import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../../api';
import type { GitProvider, VerifyGitProviderResult } from '../../api';
import type { GitRepositories, RepositoryItem, RepositorySource } from './useGitRepositories';
import { gitProviderHosts, gitProviderNames } from './useGitRepositories';
import { BitbucketIcon, GithubIcon, GitlabIcon, SpinnerIcon } from './icons';
import { Icon } from '../icons';
import { FieldError, Input, OptionCard, OptionGroup } from '../primitives';

/*
 * The repository selector of project onboarding, shared by the Magento
 * wizard and the WordPress onboarding page (CO-69): choose a Git provider,
 * connect it, pick a repository the connection can read — or type a clone
 * URL instead. Nothing here knows about a platform: what a repository must
 * contain, and whether one is required at all, is decided by the page.
 */

const providerOptions: { id: GitProvider; icon: ReactNode }[] = [
  { id: 'github', icon: <GithubIcon size={18} /> },
  { id: 'gitlab', icon: <GitlabIcon size={18} /> },
  { id: 'bitbucket', icon: <BitbucketIcon size={18} /> },
];

/** GitProviderChoice is the row of providers with their connection state. */
export function GitProviderChoice({
  git,
  onChange,
  disabled,
}: {
  git: GitRepositories;
  /** Called after the provider changed; the page drops a selection that
   * belonged to the previous provider. */
  onChange?: (provider: GitProvider) => void;
  disabled?: boolean;
}) {
  // One choice among three: option cards (audit F3), laid out three to a
  // row down to 160 px each, since a provider card is only a name and a state.
  return (
    <div className="onboarding-providers">
      <OptionGroup label="Git provider">
        {providerOptions.map((p) => {
          const active = git.provider === p.id;
          const conn = git.connectedProviders[p.id];
          const account = git.providerAccounts[p.id];
          const viaApp = p.id === 'github' && git.githubAppInstalled;
          return (
            <OptionCard
              key={p.id}
              disabled={disabled}
              selected={active}
              selectedLabel="Selected"
              onSelect={() => {
                git.setProvider(p.id);
                onChange?.(p.id);
              }}
              title={
                <span className="onboarding-provider">
                  {p.icon}
                  {gitProviderNames[p.id]}
                </span>
              }
              description={
                <span className={`onboarding-provider__state${conn ? ' onboarding-provider__state--connected' : ''}`}>
                  {conn ? <Icon name="check" size={16} /> : null}
                  {conn ? (viaApp ? 'GitHub App, connected' : account ? `${account}, connected` : 'Connected') : 'Not connected'}
                </span>
              }
            />
          );
        })}
      </OptionGroup>
    </div>
  );
}

/** ProviderConnectPrompt is shown while the chosen provider has no connection. */
export function ProviderConnectPrompt({
  git,
  onManual,
  description = 'Commerce Ops needs read access to list your repositories and branches and to check the code out on the server when it deploys. The token is stored encrypted on the project, never in your browser.',
}: {
  git: GitRepositories;
  onManual?: () => void;
  description?: string;
}) {
  const name = gitProviderNames[git.provider];
  // An empty state inside the step: the one action that fills it is a
  // secondary button, so the step's own Continue stays the only primary.
  return (
    <div className="empty-state onboarding-connect">
      <Icon name="git-branch" size={20} className="onboarding-connect__icon" />
      <h3 className="onboarding-connect__title">Connect your {name} account</h3>
      <p className="onboarding-connect__text">{description}</p>
      {git.loadingRepos ? (
        <p role="status" className="onboarding-status">
          <Icon name="spinner" size={16} /> Checking the {name} connection…
        </p>
      ) : null}
      {git.repoListError ? (
        <p role="alert" className="onboarding-status onboarding-status--danger">
          <Icon name="alert-circle" size={16} />
          <span>
            {name} could not list repositories: {git.repoListError}
          </span>
        </p>
      ) : null}
      <div className="empty-state__actions">
        <button type="button" className="button button--secondary" onClick={() => git.handleConnectProvider(git.provider)}>
          Connect account
        </button>
        {onManual ? (
          <button type="button" className="button button--ghost" onClick={onManual}>
            Enter manual URL
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * RepositoryList lists what the connection can read: a filter, the
 * loading, error and empty states, and one radio per repository. Every
 * repository is listed — a hint (Magento's "Magento detected") may be
 * added through `hint`, but nothing is filtered by platform.
 *
 * It reads any RepositorySource: the onboarding pages' useGitRepositories
 * (a token typed on the page, or the GitHub App) and, once a project
 * exists, useProjectRepositories (the project's stored repository access).
 */
export function RepositoryList({
  git,
  selected,
  onSelect,
  label = 'Repository',
  filterLabel = 'Filter repositories',
  hint,
  compact = false,
  testId,
}: {
  git: RepositorySource;
  selected: string;
  onSelect: (repository: RepositoryItem) => void;
  label?: string;
  filterLabel?: string;
  hint?: (repository: RepositoryItem) => ReactNode;
  compact?: boolean;
  testId?: string;
}) {
  const [query, setQuery] = useState('');
  const { repositories, loadingRepos, repoListError } = git;
  const filtered = repositories.filter((r) => !query || r.fullName.toLowerCase().includes(query.toLowerCase()));
  const name = gitProviderNames[git.provider];
  const credentialProblem = repoListError && /401|403|credential|token|expired|revoked|unauthori/i.test(repoListError);

  return (
    <div data-testid={testId} className={`repo-picker${compact ? ' repo-picker--compact' : ''}`}>
      <div className="repo-picker__toolbar">
        <Input
          type="text"
          className="repo-picker__filter"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={filterLabel}
          aria-label={filterLabel}
        />
        <button type="button" className="button button--ghost button--small" onClick={git.reload} disabled={loadingRepos} aria-label={`Reload repositories from ${name}`}>
          Refresh
        </button>
      </div>

      {repoListError && (
        <div role="alert" className="repo-picker__error">
          <span>{repoListError}</span>
          {credentialProblem && git.handleConnectProvider ? (
            <button type="button" className="button button--secondary button--small" onClick={() => git.handleConnectProvider?.(git.provider)}>
              Reconnect {name}
            </button>
          ) : (
            <button type="button" className="button button--secondary button--small" onClick={git.reload}>
              Try again
            </button>
          )}
        </div>
      )}
      <div role="radiogroup" aria-label={label} aria-busy={loadingRepos} className="repo-picker__list">
        {loadingRepos ? (
          <div role="status" className="repo-picker__empty">
            <SpinnerIcon size={compact ? 14 : 16} /> Loading repositories from {name}…
          </div>
        ) : filtered.length === 0 ? (
          <div className="repo-picker__empty">
            {repositories.length === 0 ? 'No repositories found for this account.' : 'No repositories match your filter.'}
          </div>
        ) : (
          filtered.map((r) => {
            const isSelected = selected === r.fullName;
            return (
              <div
                key={r.fullName}
                className="repo-option"
                role="radio"
                tabIndex={0}
                aria-checked={isSelected}
                onClick={() => onSelect(r)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(r);
                  }
                }}
              >
                <span aria-hidden="true" className="repo-option__radio" />
                <span className="repo-option__text">
                  <span className="repo-option__name">{r.fullName}</span>
                  <span className="repo-option__meta">
                    {r.isPrivate ? 'Private' : 'Public'} · updated {r.updated} · {r.branches.length} {r.branches.length === 1 ? 'branch' : 'branches'}
                  </span>
                </span>
                {hint?.(r)}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * ManualRepositoryUrl is the alternative to picking from a connection: a
 * clone URL typed by hand, optionally checked with the chosen provider's
 * credential before anything is created.
 */
export function ManualRepositoryUrl({
  git,
  value,
  onChange,
  onBack,
  onVerified,
  placeholderName = 'repository',
  inputId,
  error,
}: {
  git: GitRepositories;
  value: string;
  onChange: (url: string) => void;
  /** Back to the provider's list; omitted when there is none to go back to. */
  onBack?: () => void;
  /** Receives the branch the provider reports as default. */
  onVerified?: (result: VerifyGitProviderResult) => void;
  placeholderName?: string;
  inputId?: string;
  /** The page's validation message for the URL, shown under the field. */
  error?: string | null;
}) {
  const generatedId = useId();
  const id = inputId ?? generatedId;
  const errorId = `${id}-error`;
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<VerifyGitProviderResult | null>(null);

  async function verify() {
    if (!value.trim()) return;
    setVerifying(true);
    setResult(null);
    try {
      const res = await api.verifyGitProvider({
        provider: git.provider,
        account: git.providerAccounts[git.provider] || '',
        token: git.providerTokens[git.provider] || '',
        repository_url: value.trim(),
      });
      setResult(res);
      onVerified?.(res);
    } catch {
      setResult({ verified: false, reason: 'unexpected_error' });
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="onboarding-manual-url">
      <div className="onboarding-manual-url__head">
        <label htmlFor={id} className="field__label">
          Repository clone URL
        </label>
        {onBack ? (
          <button type="button" className="button button--ghost button--small" onClick={onBack}>
            Select from Git provider
          </button>
        ) : null}
      </div>
      <div className="onboarding-inline">
        <Input
          id={id}
          type="text"
          className="onboarding-mono onboarding-inline__grow"
          value={value}
          spellCheck={false}
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => {
            onChange(e.target.value);
            setResult(null);
          }}
          placeholder={`git@${gitProviderHosts[git.provider]}:organization/${placeholderName}.git`}
        />
        <button type="button" className="button button--secondary" onClick={verify} disabled={verifying || !value.trim()}>
          <Icon name={verifying ? 'spinner' : 'shield'} size={16} />
          Verify URL
        </button>
      </div>
      <FieldError id={errorId} message={error} />
      {result && (
        <div role="status">
          {result.verified ? (
            <span className="onboarding-status onboarding-status--success">
              <Icon name="check-circle" size={16} />
              <span>
                Repository verified{result.default_branch ? <> · Default branch: <code>{result.default_branch}</code></> : null}
              </span>
            </span>
          ) : (
            <span className="onboarding-status onboarding-status--danger">
              <Icon name="alert-circle" size={16} />
              <span>Could not verify repository access ({result.reason || 'check URL and credentials'}).</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
